/**
 * Local AI generation (3D models, speech, music, sound effects): the shared
 * settings, the approval rules and the registry of generators.
 *
 * Every local generation goes through the same pipeline:
 *
 *   request -> [approval] -> machine-wide queue (one job at a time) -> run -> [review]
 *
 * Who answers the two brackets depends on two independent switches:
 *   - autonomous mode (the assistant works on its own), and
 *   - auto-approve (nothing waits for a decision).
 */

export const GEN_KINDS = ['model', 'pixel', 'speech', 'music', 'sfx', 'video'] as const;
export type GenKind = (typeof GEN_KINDS)[number];
/** Kinds that produce a WAV through the python audio engines. */
export type AudioKind = 'speech' | 'music' | 'sfx';

export const GEN_KIND_LABEL: Record<GenKind, string> = {
  model: '3D models',
  pixel: 'Pixel art',
  speech: 'Speech',
  music: 'Music',
  sfx: 'Sound effects',
  video: 'Video',
};

/** Per-type switches (project setting `.prism/ai-settings.json`). */
export interface GenTypeSetting {
  /** Local generation may be used for this type (off: tool hidden, calls refused). */
  readonly allow: boolean;
  /** Use ONLY local generation for this type (everything of this type is made locally). */
  readonly only: boolean;
  /** Requests and results of this type are approved automatically (like the global switch, per type). */
  readonly autoApprove?: boolean;
}

export interface GenSettings {
  readonly types: Record<GenKind, GenTypeSetting>;
  /** Nothing waits for a human or the assistant to decide: requests and results are approved. */
  readonly autoApprove: boolean;
  /** Mirror of the Assistant's autonomous mode (written by the server). */
  readonly autonomous: boolean;
}

export const DEFAULT_GEN_SETTINGS: GenSettings = {
  types: {
    model: { allow: true, only: false },
    pixel: { allow: true, only: false },
    speech: { allow: true, only: false },
    music: { allow: true, only: false },
    sfx: { allow: true, only: false },
    video: { allow: true, only: false },
  },
  autoApprove: false,
  autonomous: false,
};

/** Who answers an approval or a review. */
export type Decider = 'human' | 'assistant' | 'auto';

/**
 * - neither switch on       -> the user decides.
 * - autonomous only         -> the assistant approves, rejects or redoes.
 * - auto-approve (global, or for the request's type) -> approved automatically; with autonomous mode the
 *                              assistant still runs every refine / chain / check it normally would.
 */
export function decider(
  settings: Pick<GenSettings, 'autoApprove' | 'autonomous'> & {
    readonly types?: Partial<Record<GenKind, Partial<GenTypeSetting>>>;
  },
  kind?: GenKind,
): Decider {
  if (settings.autoApprove || (kind !== undefined && settings.types?.[kind]?.autoApprove === true)) return 'auto';
  return settings.autonomous ? 'assistant' : 'human';
}

/** Lifecycle of one generation request (`.prism/gen/<id>.json`). */
export type GenStatus =
  | 'awaiting-approval' // before the queue
  | 'rejected' // approval denied: never queued
  | 'queued'
  | 'running'
  | 'review' // finished, waiting for accept / deny / redo
  | 'accepted'
  | 'denied' // result thrown away (the previous file is restored)
  | 'redo' // replaced by a new request (from the original request)
  | 'refined' // kept as the base of a refine request (built on this result)
  | 'failed';

export interface GenRequest {
  readonly id: string;
  readonly kind: GenKind;
  readonly status: GenStatus;
  /** Short description shown in the UI ("axe, TripoSR then TRELLIS.2"). */
  readonly title: string;
  readonly prompt: string;
  /** Project path the result is written to. */
  readonly out: string;
  /** Generator / engine ids, in order (a chain has more than one). */
  readonly stages: readonly string[];
  readonly decider: Decider;
  readonly requestedBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Position in the machine-wide queue while queued. */
  readonly ahead?: number;
  /** Where the running job is (stage index / name). */
  readonly progress?: string;
  readonly error?: string;
  /** Files of the finished result (project paths), for review. */
  readonly files?: readonly string[];
  /** Preview image (project path) for review cards. */
  readonly preview?: string;
  /** Set when this request replaced an earlier one (redo). */
  readonly redoOf?: string;
  /** Set when this request refines an earlier result (its output is this request's input). */
  readonly refineOf?: string;
  /** Redo count in the redo chain. */
  readonly attempt?: number;
  readonly note?: string;
  /** Per-stage settings, aligned with `stages` (pixel pipelines). */
  readonly stageParams?: readonly Record<string, unknown>[];
  /** Raw tool arguments, so a redo can re-run them with changes. */
  readonly args?: Record<string, unknown>;
  /** Recent progress lines of the running job. */
  readonly log?: readonly string[];
  /** Job kind detail: queue label. */
  readonly queueLabel?: string;
}

/** A decision written next to a request (`<id>.decision.json`) by the editor or the assistant. */
export interface GenDecisionFile {
  readonly action: GenDecision;
  readonly by: 'human' | 'assistant' | 'auto';
  readonly at: string;
  readonly note?: string;
  /** Redo: use these instead of the original request. */
  readonly stages?: readonly string[];
  readonly stageParams?: readonly Record<string, unknown>[];
  readonly prompt?: string;
  readonly seed?: number;
}

/**
 * approve / reject: before the queue. accept / deny / redo / refine: after the result.
 * redo starts over from the original request (replacing the result); refine keeps
 * the result and builds on it (it becomes the next stages' input).
 */
export type GenDecision = 'approve' | 'reject' | 'accept' | 'deny' | 'redo' | 'refine';

/* ------------------------------------------------------------------ *
 * Generators
 * ------------------------------------------------------------------ */

/** What a generator takes in and gives out (a chain feeds each output into the next input). */
export type GenIO = 'text' | 'image' | 'mesh' | 'video';

/** A configurable setting of a pipeline stage (shown as a control in the editor). */
export interface GenParam {
  readonly key: string;
  readonly label: string;
  readonly type: 'number' | 'select' | 'bool' | 'text' | 'size';
  readonly default: number | string | boolean;
  readonly min?: number;
  readonly max?: number;
  readonly step?: number;
  readonly options?: readonly { readonly value: string; readonly label: string }[];
  readonly hint?: string;
  /**
   * type 'size': a Width x Height pair. `key` is the width and `heightKey` the height (default `heightDefault`).
   * `min` / `max` bound each side; with `auto`, 0 x 0 means "automatic". `presets` are quick buttons and
   * `maxPixels` limits width x height. Any whole numbers inside the bounds can be typed.
   */
  readonly heightKey?: string;
  readonly heightDefault?: number;
  readonly auto?: boolean;
  readonly presets?: readonly (readonly [number, number])[];
  readonly maxPixels?: number;
}

/** What a stage does in a pipeline. `export` passes the image on and also writes an extra PNG (a 4K copy). */
export type GenRole = 'generate' | 'process' | 'upscale' | 'export';

export interface GeneratorInfo {
  readonly id: string;
  readonly kind: GenKind;
  readonly label: string;
  /** 1 (slowest) .. 5 (fastest). */
  readonly speed: number;
  /** 1 (rough) .. 5 (best). */
  readonly quality: number;
  /** Rough time on an 8 GB GPU. */
  readonly time: string;
  /** What it is best at. */
  readonly specialty: string;
  readonly takes: GenIO;
  readonly gives: GenIO;
  /** ComfyUI workflow file (API format) in engine/comfy, or a python engine id for audio. */
  readonly workflow: string;
  /** Models / nodes the generator needs (shown when it is not installed). */
  readonly needs: string;
  readonly license: string;
  /** Result has materials / textures (otherwise shape only). */
  readonly textured: boolean;
  /** generate (makes the asset), process (changes an image), upscale. Default: generate. */
  readonly role?: GenRole;
  /** Settings of the stage; their values go in the request's `stageParams`. */
  readonly params?: readonly GenParam[];
}

export const SPEED_LABEL = ['', 'Very slow', 'Slow', 'Medium', 'Fast', 'Very fast'] as const;
export const QUALITY_LABEL = ['', 'Rough', 'Fair', 'Good', 'High', 'Best'] as const;

export const MODEL_GENERATORS: readonly GeneratorInfo[] = [
  {
    id: 'sdxl',
    kind: 'model',
    label: 'SDXL concept image',
    speed: 4,
    quality: 4,
    time: '~15 s',
    specialty: 'Turns a text prompt into the concept image the 3D generators start from.',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-image.json',
    needs: 'checkpoints/sd_xl_base_1.0.safetensors',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
  },
  {
    id: 'trellis2',
    kind: 'model',
    label: 'TRELLIS.2',
    speed: 1,
    quality: 5,
    time: '5-10 min',
    specialty: 'Best all-round: detailed, textured organic and complex subjects (creatures, characters, vehicles, props).',
    takes: 'image',
    gives: 'mesh',
    workflow: 'image-to-3d.json',
    needs: 'TRELLIS.2 INT8 + shape/texture VAEs + DINOv3 + BiRefNet (see engine/comfy/README.md)',
    license: 'MIT',
    textured: true,
  },
  {
    id: 'hunyuan3d21',
    kind: 'model',
    label: 'Hunyuan3D 2.1 (shape)',
    speed: 3,
    quality: 4,
    time: '1-3 min',
    specialty: 'Clean, accurate geometry for hard-surface objects, buildings, tools and props. Shape only (no texture).',
    takes: 'image',
    gives: 'mesh',
    workflow: 'image-to-3d-hunyuan21.json',
    needs: 'checkpoints/hunyuan_3d_v2.1.safetensors',
    license: 'Tencent Hunyuan Community (not for EU/UK/South Korea)',
    textured: false,
  },
  {
    id: 'hunyuan3d2',
    kind: 'model',
    label: 'Hunyuan3D 2.0 (shape)',
    speed: 4,
    quality: 3,
    time: '30-90 s',
    specialty: 'Faster draft shapes for props and architecture; good for trying ideas before a slow high-quality pass. Shape only.',
    takes: 'image',
    gives: 'mesh',
    workflow: 'image-to-3d-hunyuan2.json',
    needs: 'checkpoints/hunyuan3d-dit-v2_fp16.safetensors',
    license: 'Tencent Hunyuan Community (not for EU/UK/South Korea)',
    textured: false,
  },
  {
    id: 'moge',
    kind: 'model',
    label: 'MoGe scene relief',
    speed: 5,
    quality: 2,
    time: '~10 s',
    specialty: 'Single photo or painting to a textured relief mesh: backdrops, cliffs, rock faces, terrain pieces. Not for freestanding objects.',
    takes: 'image',
    gives: 'mesh',
    workflow: 'image-to-mesh-moge.json',
    needs: 'geometry_estimation/moge_2_vitl_normal_fp16.safetensors',
    license: 'MIT',
    textured: true,
  },
];

/** Audio engines (python, run from the audio tools folder). */
export const AUDIO_GENERATORS: readonly GeneratorInfo[] = [
  {
    id: 'kokoro',
    kind: 'speech',
    label: 'Kokoro-82M',
    speed: 5,
    quality: 4,
    time: '~1 s per line',
    specialty: 'Fast, natural narration and NPC lines with ~50 built-in voices and several languages.',
    takes: 'text',
    gives: 'text',
    workflow: 'kokoro',
    needs: '<audio tools folder>/env-speech',
    license: 'Apache-2.0',
    textured: false,
  },
  {
    id: 'chatterbox',
    kind: 'speech',
    label: 'Chatterbox',
    speed: 3,
    quality: 5,
    time: '5-15 s per line',
    specialty: 'Most expressive speech: emotion control and cloning any voice from a short reference clip.',
    takes: 'text',
    gives: 'text',
    workflow: 'chatterbox',
    needs: '<audio tools folder>/env-speech',
    license: 'MIT',
    textured: false,
  },
  {
    id: 'ace-step',
    kind: 'music',
    label: 'ACE-Step',
    speed: 3,
    quality: 4,
    time: '~1-3 min per track',
    specialty: 'Full songs, instrumental or with vocals, from style tags and lyrics (52 game-oriented style presets).',
    takes: 'text',
    gives: 'text',
    workflow: 'ace-step',
    needs: '<audio tools folder>/env-music',
    license: 'Apache-2.0',
    textured: false,
  },
  {
    id: 'stable-audio',
    kind: 'sfx',
    label: 'Stable Audio Open',
    speed: 3,
    quality: 4,
    time: '~20-60 s',
    specialty: 'Realistic sound effects and ambience loops up to 47 s (83 presets: footsteps, impacts, weather, creatures).',
    takes: 'text',
    gives: 'text',
    workflow: 'stable-audio',
    needs: '<audio tools folder>/env-sfx + accepted Hugging Face license',
    license: 'Stability AI Community (free under $1M revenue)',
    textured: false,
  },
];

/** Largest pixel-art grid (a side of the finished sprite). */
export const MAX_SPRITE_SIDE = 1024;
/** Largest image side / area a hi-res image pipeline may produce (RAM and disk sanity). */
export const MAX_IMAGE_SIDE = 16384;
export const MAX_IMAGE_PIXELS = 100_000_000;

const PIXEL_SIZE: GenParam = { key: 'size', label: 'Size (px)', type: 'number', default: 64, min: 8, max: MAX_SPRITE_SIDE, step: 8, hint: 'Longest side of the pixel grid. Ignored when a final Width x Height is set.' };
const SQUARES = [16, 32, 48, 64, 128, 256, 512] as const;
/** The "Final size" of a sprite: exact W x H of the finished pixel art (0 x 0 = automatic from Size). */
const FINAL_SIZE: GenParam = {
  key: 'width',
  heightKey: 'height',
  label: 'Final size',
  type: 'size',
  default: 0,
  heightDefault: 0,
  auto: true,
  min: 1,
  max: MAX_SPRITE_SIDE,
  presets: SQUARES.map((n) => [n, n] as const),
  hint: 'Exact width x height of the finished sprite in pixels (type any size, 1-1024). The art is pixelized to fit and placed on a transparent canvas of exactly this size.',
};
const FIT_PARAM: GenParam = {
  key: 'fit',
  label: 'Fit to final size',
  type: 'select',
  default: 'contain-pad',
  options: [
    { value: 'contain-pad', label: 'Contain (pad with transparency)' },
    { value: 'crop', label: 'Fill (crop the overflow)' },
    { value: 'stretch', label: 'Stretch (may distort)' },
  ],
};
const ALIGN_PARAM: GenParam = {
  key: 'align',
  label: 'Align',
  type: 'select',
  default: 'center',
  options: ['center', 'top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'].map((v) => ({ value: v, label: v })),
};

/** Sizes offered as quick buttons for hi-res images. */
const HIRES_PRESETS: readonly (readonly [number, number])[] = [
  [1920, 1080],
  [2048, 2048],
  [2560, 1440],
  [3840, 2160],
  [4096, 4096],
  [7680, 4320],
  [8192, 8192],
];
const GEN_SIZES: readonly (readonly [number, number])[] = [
  [1024, 1024],
  [1216, 832],
  [832, 1216],
  [1344, 768],
  [768, 1344],
  [1536, 1024],
];
const SDXL_CKPT = 'sd_xl_base_1.0.safetensors';
export const PHOTO_SUFFIX = ', RAW photo, 85mm lens, natural light, shallow depth of field, fine film grain, high detail, sharp focus, realistic skin and material textures, professional photography';
export const PHOTO_NEGATIVE = 'cartoon, illustration, painting, drawing, cgi, 3d render, plastic skin, oversaturated, blurry, lowres, deformed, extra fingers, watermark, text, jpeg artifacts';
export const IMAGE_SUFFIX = ', highly detailed, sharp focus, intricate details, professional quality, beautiful lighting';
export const IMAGE_NEGATIVE = 'blurry, lowres, jpeg artifacts, deformed, watermark, text, signature, cropped, bad anatomy';

/** Photoreal checkpoints (Hugging Face SG161222, openrail++), in ComfyUI models/checkpoints. */
export const REALVIS_CKPT = 'RealVisXL_V5.0_fp16.safetensors';
export const REALVIS_FAST_CKPT = 'RealVisXL_V5.0_Lightning_fp16.safetensors';
export const REALVIS_NEGATIVE = '(octane render, render, drawing, anime, bad photo, bad photography:1.3), (worst quality, low quality, blurry:1.2), (bad teeth, deformed teeth, deformed lips), (bad anatomy, bad proportions:1.1), (deformed iris, deformed pupils), (deformed eyes, bad eyes), (deformed face, ugly face, bad face), (deformed hands, bad hands, fused fingers), morbid, mutilated, mutation, disfigured, watermark, text';

const SDXL_IMAGE_PARAMS = (suffix: string, negative: string, cfg: number, sampler: string, ckpt = SDXL_CKPT, steps = 32, cfgMin = 1): GenParam[] => [
  { key: 'width', heightKey: 'height', heightDefault: 1024, label: 'Generate size', type: 'size', default: 1024, min: 512, max: 1536, step: 64, presets: GEN_SIZES, hint: 'Native size SDXL draws at (512-1536, multiples of 64). The final size is set by the "Fit to exact size" stage.' },
  { key: 'steps', label: 'Steps', type: 'number', default: steps, min: 3, max: 80, step: 1 },
  { key: 'cfg', label: 'Guidance', type: 'number', default: cfg, min: cfgMin, max: 15, step: 0.5 },
  { key: 'sampler', label: 'Sampler', type: 'select', default: sampler, options: [{ value: 'dpmpp_2m', label: 'DPM++ 2M' }, { value: 'dpmpp_2m_sde', label: 'DPM++ 2M SDE (more natural)' }, { value: 'dpmpp_sde', label: 'DPM++ SDE (RealVisXL, slower per step)' }, { value: 'euler', label: 'Euler' }] },
  { key: 'checkpoint', label: 'Checkpoint', type: 'text', default: ckpt, hint: 'SDXL checkpoint file in ComfyUI models/checkpoints (a photoreal one such as RealVisXL or Juggernaut XL works here).' },
  { key: 'promptSuffix', label: 'Style words', type: 'text', default: suffix, hint: 'Appended to your prompt.' },
  { key: 'negative', label: 'Negative prompt', type: 'text', default: negative },
];

/**
 * Pixel art pipeline (ComfyUI image workflows + the python tools in <audio tools folder>/pixel_process.py).
 * A pipeline is one "generate" stage followed by any "process" / "upscale" stages;
 * each stage's output image is the next stage's input, and the final image
 * becomes a `.pixel.json` document (palette + layers) for the Pixel tab.
 */
export const PIXEL_GENERATORS: readonly GeneratorInfo[] = [
  {
    id: 'pixel-sdxl',
    kind: 'pixel',
    label: 'SDXL + Pixel Art XL',
    speed: 2,
    quality: 5,
    time: '~25-40 s',
    specialty: 'Best pixel-art look: characters, items, tiles and scenes with clean shapes and strong style (SDXL with the Pixel Art XL LoRA).',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-pixel-sdxl.json',
    needs: 'checkpoints/sd_xl_base_1.0.safetensors + loras/pixel-art-xl.safetensors',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
    params: [
      { key: 'width', label: 'Width', type: 'number', default: 1024, min: 512, max: 1536, step: 64 },
      { key: 'height', label: 'Height', type: 'number', default: 1024, min: 512, max: 1536, step: 64 },
      { key: 'steps', label: 'Steps', type: 'number', default: 28, min: 8, max: 60, step: 1 },
      { key: 'cfg', label: 'Guidance', type: 'number', default: 7, min: 1, max: 15, step: 0.5 },
      { key: 'loraStrength', label: 'Pixel style', type: 'number', default: 1, min: 0, max: 1.5, step: 0.05 },
    ],
  },
  {
    id: 'pixel-lightning',
    kind: 'pixel',
    label: 'SDXL Lightning + Pixel Art XL',
    speed: 5,
    quality: 3,
    time: '~6-10 s',
    specialty: 'Very fast pixel-art drafts (4 steps) for trying many ideas; a little less detailed and stable than the full model.',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-pixel-lightning.json',
    needs: 'sd_xl_base_1.0 + loras/pixel-art-xl.safetensors + loras/sdxl_lightning_4step_lora.safetensors',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
    params: [
      { key: 'width', label: 'Width', type: 'number', default: 1024, min: 512, max: 1536, step: 64 },
      { key: 'height', label: 'Height', type: 'number', default: 1024, min: 512, max: 1536, step: 64 },
      { key: 'loraStrength', label: 'Pixel style', type: 'number', default: 1, min: 0, max: 1.5, step: 0.05 },
    ],
  },
  {
    id: 'pixelize',
    kind: 'pixel',
    label: 'Pixelize (to a pixel grid)',
    speed: 5,
    quality: 4,
    time: '<1 s',
    specialty: 'Turns a smooth image into real pixel art: crops the subject, shrinks it to a clean grid, snaps colors to a limited palette.',
    takes: 'image',
    gives: 'image',
    workflow: 'pixelize',
    needs: '<audio tools folder>/pixel_process.py',
    license: 'n/a',
    textured: false,
    role: 'process',
    params: [
      PIXEL_SIZE,
      FINAL_SIZE,
      FIT_PARAM,
      ALIGN_PARAM,
      { key: 'colors', label: 'Colors', type: 'number', default: 24, min: 2, max: 62, step: 1, hint: 'Palette size (the editor holds up to 62).' },
      { key: 'method', label: 'Downscale', type: 'select', default: 'mode', options: [{ value: 'mode', label: 'Most common color (crisp)' }, { value: 'box', label: 'Average (soft)' }, { value: 'nearest', label: 'Nearest' }] },
      { key: 'dither', label: 'Dither', type: 'select', default: 'none', options: [{ value: 'none', label: 'None' }, { value: 'bayer', label: 'Ordered (Bayer)' }, { value: 'floyd', label: 'Floyd-Steinberg' }] },
      { key: 'removeBackground', label: 'Remove background', type: 'bool', default: true },
      { key: 'outline', label: 'Outline', type: 'select', default: 'none', options: [{ value: 'none', label: 'None' }, { value: 'dark', label: 'Dark outline' }, { value: 'selective', label: 'Selective (darker edge color)' }] },
    ],
  },
  {
    id: 'image-sdxl',
    kind: 'pixel',
    label: 'Hi-res image: SDXL (illustration)',
    speed: 2,
    quality: 4,
    time: '~35-45 s',
    specialty: 'Illustrations and concept art for a plain PNG (no pixel LoRA). Pair with Real-ESRGAN, the detail pass and "Fit to exact size" to reach 2K, 4K, 8K or any size you type.',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-image-sdxl.json',
    needs: 'checkpoints/sd_xl_base_1.0.safetensors',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
    params: SDXL_IMAGE_PARAMS(IMAGE_SUFFIX, IMAGE_NEGATIVE, 7, 'dpmpp_2m'),
  },
  {
    id: 'photo-sdxl',
    kind: 'pixel',
    label: 'Hi-res image: SDXL base photo',
    speed: 2,
    quality: 3,
    time: '~40-50 s',
    specialty: 'Photographs with the plain SDXL base model and photo prompting (lens, light, film grain). Fallback when the RealVisXL photoreal models are not installed; they look clearly more real.',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-image-sdxl.json',
    needs: 'checkpoints/sd_xl_base_1.0.safetensors (or any SDXL photoreal checkpoint you set)',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
    params: SDXL_IMAGE_PARAMS(PHOTO_SUFFIX, PHOTO_NEGATIVE, 6, 'dpmpp_2m_sde'),
  },
  {
    id: 'photo-realvis',
    kind: 'pixel',
    label: 'Photoreal (best): RealVisXL V5.0',
    speed: 2,
    quality: 5,
    time: '~60-80 s',
    specialty: 'The most realistic photos: real skin pores, eyes, hair and fabric, natural light. Best for portraits and people, good for places and products. 30 steps DPM++ SDE. Pair with the detail pass and "Fit to exact size" for 4K-8K.',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-image-sdxl.json',
    needs: 'checkpoints/RealVisXL_V5.0_fp16.safetensors (huggingface.co/SG161222/RealVisXL_V5.0, 6.9 GB)',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
    params: SDXL_IMAGE_PARAMS(PHOTO_SUFFIX, REALVIS_NEGATIVE, 5, 'dpmpp_sde', REALVIS_CKPT, 30),
  },
  {
    id: 'photo-realvis-fast',
    kind: 'pixel',
    label: 'Photoreal (fast): RealVisXL V5.0 Lightning',
    speed: 5,
    quality: 4,
    time: '~10-15 s',
    specialty: 'Fast high-quality photos in 5 steps (Lightning, guidance 1.5): nearly the look of the best model with slightly less fine skin and fabric detail. Great for drafts and many variations.',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-image-sdxl.json',
    needs: 'checkpoints/RealVisXL_V5.0_Lightning_fp16.safetensors (huggingface.co/SG161222/RealVisXL_V5.0_Lightning, 6.9 GB)',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
    params: SDXL_IMAGE_PARAMS(PHOTO_SUFFIX, REALVIS_NEGATIVE, 1.5, 'dpmpp_sde', REALVIS_FAST_CKPT, 5),
  },
  {
    id: 'detail-refine',
    kind: 'pixel',
    label: 'Detail pass (tiled refine)',
    speed: 1,
    quality: 5,
    time: '~15 s per 1024 tile (about 6 min at 4K)',
    specialty: 'Adds real texture to an upscaled image: low-denoise SDXL img2img over overlapping tiles, blended without seams. Removes the smooth upscaler look from skin, fabric, foliage. Very slow; runs in 8 GB.',
    takes: 'image',
    gives: 'image',
    workflow: 'tile-refine-sdxl.json',
    needs: 'checkpoints/sd_xl_base_1.0.safetensors + pixel_process.py',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'process',
    params: [
      { key: 'denoise', label: 'Strength', type: 'number', default: 0.25, min: 0.05, max: 0.6, step: 0.05, hint: 'Higher adds more new detail but may change faces and small objects.' },
      { key: 'steps', label: 'Steps', type: 'number', default: 24, min: 8, max: 60, step: 1 },
      { key: 'cfg', label: 'Guidance', type: 'number', default: 5, min: 1, max: 12, step: 0.5 },
      { key: 'tile', label: 'Tile (px)', type: 'number', default: 1024, min: 512, max: 1024, step: 64, hint: 'Smaller tiles use less memory and are not faster overall.' },
      { key: 'overlap', label: 'Tile overlap (px)', type: 'number', default: 128, min: 32, max: 256, step: 16 },
      { key: 'checkpoint', label: 'Checkpoint', type: 'text', default: SDXL_CKPT },
      { key: 'promptSuffix', label: 'Style words', type: 'text', default: ', highly detailed, sharp focus, fine natural texture' },
      { key: 'negative', label: 'Negative prompt', type: 'text', default: 'blurry, lowres, smooth plastic, jpeg artifacts, watermark, text' },
    ],
  },
  {
    id: 'fit-size',
    kind: 'pixel',
    label: 'Fit to exact size (any W x H)',
    speed: 3,
    quality: 5,
    time: '~10 s per 4x pass + resize',
    specialty: 'Reaches exactly the Width x Height you type, 2K, 4K, 8K or any odd aspect: runs Real-ESRGAN 4x passes while the image is much smaller than the target, then resizes with Lanczos and crops or pads to the exact size.',
    takes: 'image',
    gives: 'image',
    workflow: 'upscale-image.json',
    needs: 'upscale_models/RealESRGAN_x4plus.pth + pixel_process.py',
    license: 'BSD-3-Clause',
    textured: false,
    role: 'upscale',
    params: [
      { key: 'width', heightKey: 'height', heightDefault: 4096, label: 'Final size', type: 'size', default: 4096, min: 16, max: MAX_IMAGE_SIDE, maxPixels: MAX_IMAGE_PIXELS, presets: HIRES_PRESETS, hint: 'Type any width and height (up to 16384 per side and 100 megapixels).' },
      { key: 'fit', label: 'When the shape differs', type: 'select', default: 'cover', options: [{ value: 'cover', label: 'Fill and crop the overflow' }, { value: 'contain', label: 'Fit inside (transparent bars)' }, { value: 'stretch', label: 'Stretch (may distort)' }] },
      { key: 'ai', label: 'AI upscale (Real-ESRGAN)', type: 'bool', default: true, hint: 'Off: Lanczos only (fast, softer).' },
    ],
  },
  {
    id: 'canvas',
    kind: 'pixel',
    label: 'Final canvas (exact W x H, pixel art)',
    speed: 5,
    quality: 5,
    time: '<1 s',
    specialty: 'Places finished pixel art on a transparent canvas of exactly Width x Height using nearest neighbour only (never blurs). Use after clean-up or palette-map when the pixelize stage should not set the size.',
    takes: 'image',
    gives: 'image',
    workflow: 'canvas',
    needs: '<audio tools folder>/pixel_process.py',
    license: 'n/a',
    textured: false,
    role: 'process',
    params: [
      { ...FINAL_SIZE, auto: false, default: 64, heightDefault: 64 },
      FIT_PARAM,
      ALIGN_PARAM,
      { key: 'scaleUp', label: 'Enlarge by whole steps to fill', type: 'bool', default: false },
    ],
  },
  {
    id: 'export-png',
    kind: 'pixel',
    label: 'Export big PNG (nearest, e.g. 4K)',
    speed: 5,
    quality: 5,
    time: '<2 s',
    specialty: 'Keeps the low-res grid as the document and also writes a crisp enlarged PNG next to it (hero.pixel.json + hero@4k.png) by whole-number nearest-neighbour upscaling to a long side you type (default 4096).',
    takes: 'image',
    gives: 'image',
    workflow: 'export-png',
    needs: '<audio tools folder>/pixel_process.py',
    license: 'n/a',
    textured: false,
    role: 'export',
    params: [
      { key: 'size', label: 'Long side (px)', type: 'number', default: 4096, min: 64, max: MAX_IMAGE_SIDE, step: 1, hint: 'Any value. 1024 -> @1k, 2048 -> @2k, 4096 -> @4k, 8192 -> @8k, otherwise @<size>.' },
      { key: 'mode', label: 'Exactness', type: 'select', default: 'integer', options: [{ value: 'integer', label: 'Whole-number factor (may end slightly under)' }, { value: 'pad', label: 'Pad to exactly the long side' }] },
    ],
  },
  {
    id: 'upscale-esrgan',
    kind: 'pixel',
    label: 'Real-ESRGAN upscale (AI)',
    speed: 4,
    quality: 4,
    time: '~3-10 s',
    specialty: 'AI upscaling 4x for painted or smooth sources, e.g. before pixelizing for cleaner detail, or to enlarge an illustration.',
    takes: 'image',
    gives: 'image',
    workflow: 'upscale-image.json',
    needs: 'upscale_models/RealESRGAN_x4plus.pth',
    license: 'BSD-3-Clause',
    textured: false,
    role: 'upscale',
    params: [{ key: 'scale', label: 'Final scale', type: 'select', default: '4', options: [{ value: '2', label: '2x' }, { value: '4', label: '4x' }] }],
  },
  {
    id: 'upscale-pixel',
    kind: 'pixel',
    label: 'Pixel-perfect upscale (nearest)',
    speed: 5,
    quality: 5,
    time: '<1 s',
    specialty: 'Enlarges finished pixel art by whole-number steps with no blurring (2x, 3x, 4x, 8x).',
    takes: 'image',
    gives: 'image',
    workflow: 'upscale-pixel',
    needs: '<audio tools folder>/pixel_process.py',
    license: 'n/a',
    textured: false,
    role: 'upscale',
    params: [{ key: 'factor', label: 'Factor', type: 'number', default: 4, min: 2, max: 16, step: 1 }],
  },
  {
    id: 'palette-map',
    kind: 'pixel',
    label: 'Palette map',
    speed: 5,
    quality: 4,
    time: '<1 s',
    specialty: 'Maps the image to a named retro palette (Sweetie 16, PICO-8, Game Boy, NES, Endesga 32, Resurrect 64…).',
    takes: 'image',
    gives: 'image',
    workflow: 'palette-map',
    needs: '<audio tools folder>/pixel_process.py',
    license: 'n/a',
    textured: false,
    role: 'process',
    params: [
      { key: 'palette', label: 'Palette', type: 'select', default: 'sweetie16', options: [{ value: 'sweetie16', label: 'Sweetie 16' }, { value: 'pico8', label: 'PICO-8' }, { value: 'gameboy', label: 'Game Boy (4)' }, { value: 'nes', label: 'NES (54)' }, { value: 'endesga32', label: 'Endesga 32' }, { value: 'resurrect64', label: 'Resurrect 64' }, { value: 'apollo', label: 'Apollo (46)' }] },
      { key: 'dither', label: 'Dither', type: 'select', default: 'none', options: [{ value: 'none', label: 'None' }, { value: 'bayer', label: 'Ordered (Bayer)' }, { value: 'floyd', label: 'Floyd-Steinberg' }] },
    ],
  },
  {
    id: 'cleanup',
    kind: 'pixel',
    label: 'Clean-up',
    speed: 5,
    quality: 3,
    time: '<1 s',
    specialty: 'Removes stray single pixels, sharpens edges and removes the background; run after pixelizing.',
    takes: 'image',
    gives: 'image',
    workflow: 'cleanup',
    needs: '<audio tools folder>/pixel_process.py',
    license: 'n/a',
    textured: false,
    role: 'process',
    params: [
      { key: 'despeckle', label: 'Remove stray pixels', type: 'bool', default: true },
      { key: 'removeBackground', label: 'Remove background', type: 'bool', default: true },
      { key: 'tolerance', label: 'Background tolerance', type: 'number', default: 12, min: 0, max: 60, step: 1 },
    ],
  },
];

/**
 * Video stages (engine/comfy/README.md "Video"). A video request runs one generator stage (it makes the clip),
 * then optional extend stages (the same model continues the clip from its last frame) and post stages
 * (interpolate, upscale). `keyframe` and `assemble` are single-stage requests.
 * Times are measured on an RTX 3060 Ti 8 GB with 16 GB RAM; "estimate" marks numbers not measured end to end.
 */
export const VIDEO_GENERATORS: readonly GeneratorInfo[] = [
  {
    id: 'keyframe',
    kind: 'video',
    label: 'Keyframe image (SDXL / RealVisXL)',
    speed: 4,
    quality: 4,
    time: '~15-80 s per still',
    specialty: 'Character sheets and shot keyframes: the start image (identity, framing, light) a clip is animated from, and the re-anchor image every few shots. Runs the hi-res image stages (photo-realvis-fast by default).',
    takes: 'text',
    gives: 'image',
    workflow: 'text-to-image-sdxl.json',
    needs: 'checkpoints/RealVisXL_V5.0_Lightning_fp16.safetensors (or sd_xl_base_1.0)',
    license: 'CreativeML Open RAIL++-M',
    textured: false,
    role: 'generate',
  },
  {
    id: 'animatic',
    kind: 'video',
    label: 'Animatic: Causal Forcing (Wan 2.1 1.3B)',
    speed: 4,
    quality: 2,
    time: '~2 min per 5 s clip idle (estimate); 11-12 min under RAM contention',
    specialty: 'Fast 480p scene outlines for storyboard review: framing, blocking and motion before the slow final pass. 832x480, 16 fps, 81 frames.',
    takes: 'text',
    gives: 'video',
    workflow: 'image-to-video-causal.json',
    needs: 'Causal Forcing framewise (Wan 2.1 1.3B) in ComfyUI models (see engine/comfy/README.md)',
    license: 'Apache-2.0',
    textured: false,
    role: 'generate',
  },
  {
    id: 'animatic-extend',
    kind: 'video',
    label: 'Animatic extend (Causal Forcing)',
    speed: 4,
    quality: 2,
    time: 'about the same as an animatic clip (not yet measured)',
    specialty: 'Continues an animatic clip from its last frame for shots longer than 5 s.',
    takes: 'video',
    gives: 'video',
    workflow: 'extend-video-causal.json',
    needs: 'as animatic',
    license: 'Apache-2.0',
    textured: false,
    role: 'process',
  },
  {
    id: 'shot',
    kind: 'video',
    label: 'Final shot: Wan 2.2 TI2V 5B (720p)',
    speed: 2,
    quality: 4,
    time: '~9-10 min per 5 s clip (estimate: 7.7 min sampling + tiled decode)',
    specialty: "Default final shots: native 1280x704 at 24 fps, 121 frames (5 s), good motion and detail. Image-to-video from a keyframe or the previous shot's last frame.",
    takes: 'text',
    gives: 'video',
    workflow: 'image-to-video-wan22-5b.json',
    needs: 'diffusion_models/wan2.2_ti2v_5B_w4a8.safetensors + umt5 text encoder + Wan 2.2 VAE',
    license: 'Apache-2.0',
    textured: false,
    role: 'generate',
  },
  {
    id: 'shot-extend',
    kind: 'video',
    label: 'Final extend (Wan 2.2 5B)',
    speed: 2,
    quality: 4,
    time: '~9-10 min per extra 5 s (not yet measured)',
    specialty: 'Continues a 5B shot from its last frame (no repeated frame) for shots longer than 5 s.',
    takes: 'video',
    gives: 'video',
    workflow: 'extend-video-wan22-5b.json',
    needs: 'as shot',
    license: 'Apache-2.0',
    textured: false,
    role: 'process',
  },
  {
    id: 'shot-hq',
    kind: 'video',
    label: 'Hero shot: Wan 2.2 I2V A14B (slow)',
    speed: 1,
    quality: 5,
    time: 'not yet measured (two 14.3 GB experts on 16 GB RAM: expect heavy swapping, 20+ min)',
    specialty: 'Best quality and motion for a few hero shots. 832x480 at 16 fps, 81 frames. Needs a start image.',
    takes: 'image',
    gives: 'video',
    workflow: 'image-to-video-wan22-14b.json',
    needs: 'Wan 2.2 I2V A14B fp8 high/low-noise experts + lightx2v 4-step LoRAs',
    license: 'Apache-2.0',
    textured: false,
    role: 'generate',
  },
  {
    id: 'shot-bridge',
    kind: 'video',
    label: 'Bridge: first + last frame (Wan 2.2 14B)',
    speed: 1,
    quality: 5,
    time: 'not yet measured (as hero shot)',
    specialty: 'Animates between two keyframes: hits an exact end pose or returns to a re-anchor keyframe.',
    takes: 'image',
    gives: 'video',
    workflow: 'first-last-frame-wan22-14b.json',
    needs: 'as hero shot',
    license: 'Apache-2.0',
    textured: false,
    role: 'generate',
  },
  {
    id: 'shot-hq-extend',
    kind: 'video',
    label: 'Hero extend (Wan 2.2 14B)',
    speed: 1,
    quality: 5,
    time: 'not yet measured',
    specialty: 'Continues a 14B shot from its last frame.',
    takes: 'video',
    gives: 'video',
    workflow: 'extend-video-wan22-14b.json',
    needs: 'as hero shot',
    license: 'Apache-2.0',
    textured: false,
    role: 'process',
  },
  {
    id: 'lipsync',
    kind: 'video',
    label: 'Lip sync: HuMo 1.7B',
    speed: 3,
    quality: 3,
    time: 'not yet measured (per 3.84 s chunk)',
    specialty: 'Talking shots: generates the speaking character from a reference face and the voice WAV (25 fps, 480p, 3.84 s chunks joined). Best for close-ups and medium shots.',
    takes: 'image',
    gives: 'video',
    workflow: 'lipsync-humo.json',
    needs: 'HuMo 1.7B fp16 + whisper-large-v3 audio encoder',
    license: 'Apache-2.0',
    textured: false,
    role: 'generate',
  },
  {
    id: 'interpolate',
    kind: 'video',
    label: 'Interpolate: RIFE 4.26',
    speed: 5,
    quality: 4,
    time: '~3.5 s for x2 on a 5 s 480p clip',
    specialty: 'Doubles the frame rate (16 to 32 fps) for smooth motion before the edit.',
    takes: 'video',
    gives: 'video',
    workflow: 'interpolate-rife.json',
    needs: 'RIFE 4.26 frame interpolation model',
    license: 'MIT',
    textured: false,
    role: 'process',
  },
  {
    id: 'interpolate-film',
    kind: 'video',
    label: 'Interpolate: FILM',
    speed: 4,
    quality: 4,
    time: 'not yet measured (seconds)',
    specialty: 'Frame interpolation that handles big motion better than RIFE.',
    takes: 'video',
    gives: 'video',
    workflow: 'interpolate-film.json',
    needs: 'FILM fp16',
    license: 'Apache-2.0',
    textured: false,
    role: 'process',
  },
  {
    id: 'upscale',
    kind: 'video',
    label: 'Upscale: SeedVR2 3B (detail restore)',
    speed: 1,
    quality: 5,
    time: 'not yet measured (slow; 64-frame tiles)',
    specialty: 'Final upscale with real detail restoration (one-step diffusion), consistent between frames.',
    takes: 'video',
    gives: 'video',
    workflow: 'upscale-video-seedvr2.json',
    needs: 'SeedVR2 3B int8 + SeedVR2 VAE',
    license: 'Apache-2.0',
    textured: false,
    role: 'upscale',
  },
  {
    id: 'upscale-fast',
    kind: 'video',
    label: 'Upscale: Real-ESRGAN per frame (fast)',
    speed: 3,
    quality: 3,
    time: 'not yet measured (image version: 11 s per 1216x832 frame)',
    specialty: 'Sharpens and enlarges each frame, then Lanczos to the exact size; can shimmer between frames.',
    takes: 'video',
    gives: 'video',
    workflow: 'upscale-video-esrgan.json',
    needs: 'upscale_models/RealESRGAN_x4plus.pth',
    license: 'BSD-3-Clause',
    textured: false,
    role: 'upscale',
  },
  {
    id: 'engine-record',
    kind: 'video',
    label: 'Engine shot: prism record',
    speed: 4,
    quality: 5,
    time: 'seconds to minutes (offline render at any resolution)',
    specialty: 'Renders an in-engine shot (scene + sequence: cameras, animations, subtitles) offscreen to video. No AI video model.',
    takes: 'text',
    gives: 'video',
    workflow: 'prism-record',
    needs: 'the prism CLI (pnpm engine:build) and ffmpeg',
    license: 'n/a',
    textured: false,
    role: 'generate',
  },
  {
    id: 'assemble',
    kind: 'video',
    label: 'Assemble: ffmpeg edit + mix',
    speed: 4,
    quality: 5,
    time: 'about 1-3 min per 10 min of video',
    specialty: 'Joins the shots (cuts and crossfades), mixes dialogue, music (ducked under speech) and SFX, normalizes loudness and writes subtitles.',
    takes: 'text',
    gives: 'video',
    workflow: 'ffmpeg',
    needs: 'ffmpeg (PRISM_FFMPEG, PATH or E:\\tools\\ffmpeg\\bin\\ffmpeg.exe)',
    license: 'n/a',
    textured: false,
    role: 'generate',
  },
];

/** Video stages that run a local AI model (governed by the "Video" allow switch); the others are plain renders. */
export const VIDEO_AI_STAGES: readonly string[] = VIDEO_GENERATORS.filter((g) => g.id !== 'engine-record' && g.id !== 'assemble').map((g) => g.id);

/** Extend stage of each clip generator (the same model continues the clip). */
export const VIDEO_EXTEND_OF: Readonly<Record<string, string>> = {
  animatic: 'animatic-extend',
  'animatic-extend': 'animatic-extend',
  shot: 'shot-extend',
  'shot-extend': 'shot-extend',
  'shot-hq': 'shot-hq-extend',
  'shot-bridge': 'shot-hq-extend',
  'shot-hq-extend': 'shot-hq-extend',
};

/** Longest clip one call of a generator makes, in frames at its native rate (README "max clip / gen"). */
export const VIDEO_NATIVE: Readonly<Record<string, { width: number; height: number; fps: number; frames: number }>> = {
  animatic: { width: 832, height: 480, fps: 16, frames: 81 },
  'animatic-extend': { width: 832, height: 480, fps: 16, frames: 81 },
  shot: { width: 1280, height: 704, fps: 24, frames: 121 },
  'shot-extend': { width: 1280, height: 704, fps: 24, frames: 121 },
  'shot-hq': { width: 832, height: 480, fps: 16, frames: 81 },
  'shot-bridge': { width: 832, height: 480, fps: 16, frames: 81 },
  'shot-hq-extend': { width: 832, height: 480, fps: 16, frames: 81 },
  lipsync: { width: 832, height: 480, fps: 25, frames: 97 },
};

/** Single-stage video requests. */
const VIDEO_ALONE = new Set(['keyframe', 'assemble']);

/**
 * A video pipeline: one generator stage (or, with `fromVideo`, none: the input clip), then the matching extend
 * stages, then post stages (interpolate / upscale). keyframe and assemble run alone.
 */
export function validateVideoPipeline(ids: readonly string[], opts: { fromVideo?: boolean } = {}): string | null {
  if (!ids.length) return 'A video pipeline needs at least one stage.';
  let last: string | null = null;
  let post = false;
  for (const [index, id] of ids.entries()) {
    const g = VIDEO_GENERATORS.find((x) => x.id === id);
    if (!g) return `Unknown video stage '${id}'.`;
    if (VIDEO_ALONE.has(id) && ids.length > 1) return `'${g.label}' runs alone.`;
    const role = g.role ?? 'generate';
    const extend = role === 'process' && !id.startsWith('interpolate');
    if (role === 'generate') {
      if (index > 0 || opts.fromVideo) return `'${g.label}' makes a new clip and can only be the first stage.`;
    } else if (extend) {
      if (post) return `'${g.label}' must come before interpolate / upscale stages.`;
      if (last === null && !opts.fromVideo) return `'${g.label}' continues a clip: start with a generator stage.`;
      if (last !== null && VIDEO_EXTEND_OF[last] !== id) return `'${g.label}' cannot continue '${last}' (use ${VIDEO_EXTEND_OF[last] ?? 'no extend stage'}).`;
    } else {
      if (last === null && !opts.fromVideo) return `'${g.label}' changes a clip: start with a generator stage.`;
      post = true;
    }
    last = id;
  }
  return null;
}

export const ALL_GENERATORS: readonly GeneratorInfo[] = [...MODEL_GENERATORS, ...PIXEL_GENERATORS, ...AUDIO_GENERATORS, ...VIDEO_GENERATORS];

/** Pipeline presets for pixel art. */
export const PIXEL_PRESETS: readonly { id: string; label: string; stages: readonly string[]; note: string; params?: readonly Record<string, number | string | boolean>[] }[] = [
  { id: 'sprite', label: 'Game sprite (best)', stages: ['pixel-sdxl', 'pixelize', 'cleanup'], note: 'Full-quality SDXL, pixelized to a clean 64 px grid and tidied.' },
  { id: 'quick-sprite', label: 'Quick sprite', stages: ['pixel-lightning', 'pixelize'], note: 'Fast draft, pixelized.' },
  { id: 'retro', label: 'Retro palette', stages: ['pixel-sdxl', 'pixelize', 'palette-map'], note: 'Pixelized and mapped to a fixed retro palette.' },
  { id: 'hires-pixel', label: 'Hi-res pixel art', stages: ['pixel-sdxl', 'upscale-esrgan', 'pixelize', 'upscale-pixel'], note: 'AI-upscale for detail, pixelize, then enlarge with perfect pixels.' },
  { id: 'pixel-4k', label: 'Pixel art + 4K PNG', stages: ['pixel-sdxl', 'pixelize', 'cleanup', 'export-png'], note: 'Fast. Keeps the real pixel grid as hero.pixel.json and also writes hero@4k.png (whole-number nearest upscale to 4096).' },
  { id: 'pixel-big-grid', label: 'Large-grid pixel art (512) + 4K PNG', stages: ['pixel-sdxl', 'upscale-esrgan', 'pixelize', 'cleanup', 'export-png'], params: [{}, {}, { size: 512, colors: 62 }], note: 'Slow. A 512 px grid with 62 colors (detailed scenes), plus a 4K PNG copy.' },
  { id: 'photo-4k', label: 'Photo 4K (best, very slow)', stages: ['photo-realvis', 'upscale-esrgan', 'detail-refine', 'fit-size'], params: [{ width: 1344, height: 768 }, {}, { checkpoint: REALVIS_CKPT }, { width: 3840, height: 2160 }], note: 'Very slow (about 9 min). RealVisXL V5.0 photo, Real-ESRGAN 4x, tiled RealVisXL detail pass for real texture, then exactly 3840 x 2160. Saves a plain PNG; change the size in the last stage.' },
  { id: 'photo-4k-fast', label: 'Photo 4K (fast)', stages: ['photo-realvis-fast', 'upscale-esrgan', 'fit-size'], params: [{ width: 1344, height: 768 }, {}, { width: 3840, height: 2160 }], note: 'About 40 s. RealVisXL V5.0 Lightning (5 steps), Real-ESRGAN 4x, exactly 3840 x 2160 (no detail pass).' },
  { id: 'image-4k', label: 'Illustration 4K (best, very slow)', stages: ['image-sdxl', 'upscale-esrgan', 'detail-refine', 'fit-size'], note: 'Very slow. SDXL at 1024, Real-ESRGAN 4x, tiled detail pass, exactly 4096 x 4096 (plain PNG).' },
  { id: 'image-2k', label: 'Illustration 2K (fast)', stages: ['image-sdxl', 'fit-size'], params: [{}, { width: 2048, height: 2048 }], note: 'About 1 min. SDXL at 1024, upscaled to exactly 2048 x 2048 (plain PNG).' },
];

export function generatorById(id: string): GeneratorInfo | undefined {
  return ALL_GENERATORS.find((g) => g.id === id);
}

/** "Fast / High quality" style tag line for UI and prompts. */
export function generatorTags(g: GeneratorInfo): string {
  return `${SPEED_LABEL[g.speed]} · ${QUALITY_LABEL[g.quality]} quality · ${g.time}`;
}

/**
 * A chain of model generators: each stage's output is the next stage's input.
 * Valid when the first stage takes text or an image, every stage takes what
 * the previous one gave (a mesh is shown to the next stage as a rendered image),
 * and the last stage gives a mesh.
 */
export function validateChain(ids: readonly string[], kind: 'model' | 'pixel' = 'model'): string | null {
  if (kind === 'pixel') return validatePixelPipeline(ids);
  if (!ids.length) return 'A chain needs at least one generator.';
  let have: GenIO | null = null;
  for (const [index, id] of ids.entries()) {
    const g = MODEL_GENERATORS.find((x) => x.id === id);
    if (!g) return `Unknown model generator '${id}'.`;
    if (have === null) {
      if (g.takes === 'mesh') return `'${g.label}' cannot start a chain.`;
    } else {
      // A mesh becomes an image (a render of it) for the next stage.
      const given: GenIO = have === 'mesh' ? 'image' : have;
      if (g.takes !== given && !(given === 'image' && g.takes === 'image'))
        return `'${g.label}' needs ${g.takes} but stage ${index} gives ${have}.`;
    }
    have = g.gives;
  }
  return have === 'mesh' ? null : 'The last stage of a chain must produce a mesh.';
}

/** Chain presets, from a quick draft to the best result. */
export const CHAIN_PRESETS: readonly { id: string; label: string; stages: readonly string[]; note: string }[] = [
  { id: 'best', label: 'Best quality', stages: ['sdxl', 'trellis2'], note: 'SDXL concept, then TRELLIS.2 (slow, textured).' },
  { id: 'fast', label: 'Fast draft', stages: ['sdxl', 'hunyuan3d2'], note: 'SDXL concept, then a quick Hunyuan3D 2.0 shape.' },
  { id: 'shape-then-polish', label: 'Draft, then polish', stages: ['sdxl', 'hunyuan3d2', 'trellis2'], note: 'Fast shape first; its render feeds TRELLIS.2 for the final textured model.' },
  { id: 'clean-geometry', label: 'Clean geometry', stages: ['sdxl', 'hunyuan3d21', 'trellis2'], note: 'Accurate Hunyuan3D 2.1 shape, rendered and polished by TRELLIS.2.' },
  { id: 'relief', label: 'Scene relief', stages: ['sdxl', 'moge'], note: 'Concept image to a relief mesh (backdrops, cliffs).' },
];

/** Stages whose result is a big photo-like image, not a sprite: they save a plain PNG. */
export const HIRES_IMAGE_STAGES: readonly string[] = ['image-sdxl', 'photo-sdxl', 'photo-realvis', 'photo-realvis-fast', 'detail-refine', 'fit-size'];

/** Suffix of an exported copy: 4096 -> '@4k'. */
export function exportSuffix(size: number): string {
  const n = Math.round(size);
  return n === 1024 ? '@1k' : n === 2048 ? '@2k' : n === 4096 ? '@4k' : n === 8192 ? '@8k' : `@${n}`;
}

/** Project path of an export: `art/hero.pixel.json` + 4096 -> `art/hero@4k.png`. */
export function pixelExportPath(out: string, size: number): string {
  return `${out.replace(/\.(pixel\.json|png)$/i, '')}${exportSuffix(size)}.png`;
}

/** Why `out` does not suit the pipeline (a hi-res image cannot be a 62-color pixel document), or null. */
export function pixelOutProblem(ids: readonly string[], out: string): string | null {
  const hires = ids.some((id) => HIRES_IMAGE_STAGES.includes(id));
  if (/\.pixel\.json$/i.test(out)) return hires ? 'A hi-res image pipeline saves a plain PNG: end the output path with .png.' : null;
  if (/\.png$/i.test(out)) return null;
  return 'The output must end with .pixel.json (pixel art) or .png (hi-res image).';
}

/**
 * Checks a stage's settings against its spec: numbers inside min/max, selects from their options, size pairs
 * inside bounds (both 0 for "automatic" where allowed) and under the pixel budget. Returns a message or null.
 * Unknown keys are ignored; missing keys use defaults.
 */
export function validateStageParams(id: string, given: Record<string, unknown> | undefined): string | null {
  const g = generatorById(id);
  if (!g) return `Unknown stage '${id}'.`;
  const v: Record<string, unknown> = { ...defaultStageParams(id), ...(given ?? {}) };
  for (const p of g.params ?? []) {
    const raw = v[p.key];
    if (p.type === 'number') {
      const n = Number(raw);
      if (!Number.isFinite(n)) return `${g.label}: ${p.label} must be a number.`;
      if ((p.min !== undefined && n < p.min) || (p.max !== undefined && n > p.max)) return `${g.label}: ${p.label} must be between ${p.min ?? '-'} and ${p.max ?? '-'}.`;
    } else if (p.type === 'select') {
      if (!(p.options ?? []).some((o) => o.value === String(raw))) return `${g.label}: ${p.label} must be one of ${(p.options ?? []).map((o) => o.value).join(', ')}.`;
    } else if (p.type === 'size') {
      const w = Number(raw);
      const h = Number(v[p.heightKey ?? 'height']);
      if (!Number.isFinite(w) || !Number.isFinite(h) || !Number.isInteger(w) || !Number.isInteger(h)) return `${g.label}: ${p.label} must be whole numbers.`;
      if (p.auto && w === 0 && h === 0) continue;
      if (p.auto && (w === 0 || h === 0)) return `${g.label}: set both width and height (or both 0 for automatic).`;
      const lo = p.min ?? 1;
      const hi = p.max ?? MAX_IMAGE_SIDE;
      if (w < lo || h < lo || w > hi || h > hi) return `${g.label}: ${p.label} must be ${lo}-${hi} px per side (got ${w} x ${h}).`;
      if (p.maxPixels !== undefined && w * h > p.maxPixels) return `${g.label}: ${p.label} is ${Math.round((w * h) / 1e6)} megapixels; the limit is ${p.maxPixels / 1e6} (too big for memory).`;
    }
  }
  return null;
}

/** Validates every stage's settings; the first problem or null. */
export function validatePipelineParams(ids: readonly string[], params: readonly (Record<string, unknown> | undefined)[] | undefined): string | null {
  for (const [i, id] of ids.entries()) {
    const bad = validateStageParams(id, params?.[i]);
    if (bad) return bad;
  }
  return null;
}

/** A pixel pipeline: one generate stage, then any number of process / upscale stages. */
export function validatePixelPipeline(ids: readonly string[], opts: { fromImage?: boolean } = {}): string | null {
  if (!ids.length) return 'A pixel pipeline needs at least one stage.';
  for (const [index, id] of ids.entries()) {
    const g = PIXEL_GENERATORS.find((x) => x.id === id);
    if (!g) return `Unknown pixel stage '${id}'.`;
    const role = g.role ?? 'generate';
    if (index === 0 && role !== 'generate' && !opts.fromImage) return `'${g.label}' changes an image: start with a generator (${PIXEL_GENERATORS.filter((x) => x.role === 'generate').map((x) => x.id).join(', ')}).`;
    if ((index > 0 || opts.fromImage) && role === 'generate') return `'${g.label}' makes a new image and can only be the first stage.`;
  }
  return null;
}

/** Default values of a stage's parameters. */
export function defaultStageParams(id: string): Record<string, number | string | boolean> {
  const g = generatorById(id);
  const out: Record<string, number | string | boolean> = {};
  for (const p of g?.params ?? []) {
    out[p.key] = p.default;
    if (p.type === 'size') out[p.heightKey ?? 'height'] = p.heightDefault ?? Number(p.default);
  }
  return out;
}
